import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { z } from 'zod';
import { Button } from './Button';
import { FormField, type ZodForm } from './Form';
import { Input } from './Input';
import {
  PasswordCriteriaList,
  type PasswordCriteria,
} from './PasswordCriteriaList';

/** Which of the password rules a value meets. */
export function passwordCriteria(password: string): PasswordCriteria {
  return {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
  };
}

export const passwordSchema = z
  .object({
    password: z
      .string()
      .min(1, 'Enter a password.')
      .refine(
        (v) => Object.values(passwordCriteria(v)).every(Boolean),
        'Your password does not meet the requirements below.'
      ),
    confirmPassword: z.string().min(1, 'Confirm your password.'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

export type PasswordValues = z.infer<typeof passwordSchema>;
export type PasswordField = keyof PasswordValues;
export type PasswordForm = ZodForm<typeof passwordSchema>;

export interface PasswordFieldsetProps {
  /** From `useZodForm(passwordSchema, …)`. */
  form: PasswordForm;
}

/**
 * A password and its confirmation, each with a show/hide toggle, the rules
 * list under the first and a mismatch line under the second. Enter on the
 * password moves to the confirmation; Enter there submits the form.
 *
 *   <PasswordFieldset form={form} />
 */
export function PasswordFieldset({ form }: PasswordFieldsetProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [passwordBlurred, setPasswordBlurred] = React.useState(false);
  const password = form.watch('password');
  const criteria = passwordCriteria(password);

  const revealButton = (shown: boolean, toggle: () => void) => (
    <Button
      size="inline"
      variant="subtle"
      type="button"
      onClick={toggle}
      className="pointer-events-auto"
      aria-label={shown ? 'Hide password' : 'Show password'}
    >
      {shown ? <EyeOff /> : <Eye />}
    </Button>
  );

  return (
    <>
      <FormField form={form} name="password" label="Password">
        {(field) => (
          <Input
            {...field}
            type={showPassword ? 'text' : 'password'}
            onBlur={() => {
              setPasswordBlurred(true);
              field.onBlur();
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              form.setFocus('confirmPassword');
            }}
            size="lg"
            trailingIcon={revealButton(showPassword, () =>
              setShowPassword(!showPassword)
            )}
          />
        )}
      </FormField>

      {password && (
        <PasswordCriteriaList criteria={criteria} flagUnmet={passwordBlurred} />
      )}

      <FormField form={form} name="confirmPassword" label="Confirm password">
        {(field) => (
          <Input
            {...field}
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm your password"
            size="lg"
            trailingIcon={revealButton(showConfirmPassword, () =>
              setShowConfirmPassword(!showConfirmPassword)
            )}
          />
        )}
      </FormField>
    </>
  );
}
